/**
 * Camera management - orthographic camera with pan/zoom
 */

import * as THREE from 'three';

export class Camera {
  public readonly camera: THREE.OrthographicCamera;
  public readonly target: THREE.Vector3;

  private frustumBase = 40;
  private _zoom = 1.0;
  private readonly minZoom = 0.35;
  private readonly maxZoom = 3.5;

  // Camera Z offset for consistent positioning
  // Reduced for more top-down view (tiles appear more square)
  private readonly cameraZOffset = 20;

  // Map bounds for camera clamping (half-size in world units)
  private mapBounds = 256;

  constructor() {
    this.camera = new THREE.OrthographicCamera(0, 0, 0, 0, 0.1, 500);
    this.target = new THREE.Vector3(0, 0, 0);

    this.updateProjection();
    // More top-down view (~53 degrees from horizontal) for square-looking tiles
    this.camera.position.set(0, 40, this.cameraZOffset);
    this.camera.lookAt(this.target);
  }

  private updateProjection(): void {
    const aspect = window.innerWidth / window.innerHeight;
    const v = this.frustumBase * this._zoom;

    this.camera.left = -v * aspect * 0.5;
    this.camera.right = v * aspect * 0.5;
    this.camera.top = v * 0.5;
    this.camera.bottom = -v * 0.5;
    this.camera.updateProjectionMatrix();
  }

  get zoom(): number {
    return this._zoom;
  }

  set zoom(value: number) {
    this._zoom = Math.max(this.minZoom, Math.min(this.maxZoom, value));
    this.updateProjection();
    // Re-clamp position as frustum size changed
    this.clampPosition();
    this.camera.position.x = this.target.x;
    this.camera.position.z = this.target.z + this.cameraZOffset;
    this.camera.lookAt(this.target);
  }

  pan(dx: number, dz: number): void {
    this.target.x += dx;
    this.target.z += dz;
    this.clampPosition();
    this.camera.position.x = this.target.x;
    this.camera.position.z = this.target.z + this.cameraZOffset;
    this.camera.lookAt(this.target);
  }

  setPosition(x: number, z: number): void {
    this.target.x = x;
    this.target.z = z;
    this.clampPosition();
    this.camera.position.x = this.target.x;
    this.camera.position.z = this.target.z + this.cameraZOffset;
    this.camera.lookAt(this.target);
  }

  /**
   * Clamp camera position so frustum stays within map bounds
   */
  private clampPosition(): void {
    const aspect = window.innerWidth / window.innerHeight;
    const frustumHeight = this.frustumBase * this._zoom;
    const frustumWidth = frustumHeight * aspect;

    // Simple bounds clamping - keep camera target within map bounds
    // with margin for the visible frustum area
    const maxX = Math.max(0, this.mapBounds - frustumWidth * 0.5);
    const maxZ = Math.max(0, this.mapBounds - frustumHeight * 0.5);

    this.target.x = Math.max(-maxX, Math.min(maxX, this.target.x));
    this.target.z = Math.max(-maxZ, Math.min(maxZ, this.target.z));
  }

  /**
   * Set map bounds for camera clamping
   * @param halfSize Half-size of the map in world units (map extends from -halfSize to +halfSize)
   */
  setMapBounds(halfSize: number): void {
    this.mapBounds = halfSize;
    this.clampPosition();
    this.camera.position.x = this.target.x;
    this.camera.position.z = this.target.z + this.cameraZOffset;
    this.camera.lookAt(this.target);
  }

  onResize(): void {
    this.updateProjection();
    // Re-clamp position as aspect ratio changed
    this.clampPosition();
    this.camera.position.x = this.target.x;
    this.camera.position.z = this.target.z + this.cameraZOffset;
    this.camera.lookAt(this.target);
  }

  getWorldPosition(screenX: number, screenY: number, groundY: number = 1.5): { x: number; z: number } {
    // Use raycasting for accurate position with tilted camera
    const ndcX = (screenX / window.innerWidth) * 2 - 1;
    const ndcY = -(screenY / window.innerHeight) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);

    // Intersect with ground plane at typical terrain height
    // Plane equation: y = groundY, so normal is (0,1,0) and constant is -groundY
    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -groundY);
    const intersection = new THREE.Vector3();

    if (raycaster.ray.intersectPlane(groundPlane, intersection)) {
      return { x: intersection.x, z: intersection.z };
    }

    // Fallback
    const aspect = window.innerWidth / window.innerHeight;
    const v = this.frustumBase * this._zoom;
    return {
      x: this.target.x + ndcX * v * aspect * 0.5,
      z: this.target.z - ndcY * v * 0.5,
    };
  }

  getFrustumSize(): { width: number; height: number } {
    const aspect = window.innerWidth / window.innerHeight;
    const v = this.frustumBase * this._zoom;
    return { width: v * aspect, height: v };
  }
}
